/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { RouterModule, Routes } from "@angular/router";
import { ServerDetailsPageComponent } from "../module/cluster/server/server-details-page.component";
import { TeamSubsiteScaffoldComponent } from "../module/scaffold/team-subsite/team-subsite-scaffold.component";
import { SetupProjectComponent } from "../module/setup/project/setup-project.component";
import { SetupComponent } from "../module/setup/setup.component";
import {
    genericGuard,
    setupCompletedGuard,
    loggedOutGuard,
    setupGuard,
    teamJoinedNotSurveyedGuard,
    getStartedOverviewGuard, mfaGuard
} from "./auth-guard";

import { _404PageComponent } from "../module/404/404-page.component";
import { Internal404PageComponent } from "../module/404/internal/internal-404-page.component";
import { ClusterDeploymentPageComponent } from "../module/cluster/deploy/cluster-deployment-page.component";
import { ClusterDetailsPageComponent } from "../module/cluster/details/cluster-details-page.component";
import { ClustersPageComponent } from "../module/cluster/page/clusters-page.component";
import { PasswordResetPageComponent } from "../module/login/password-reset/password-reset-page.component";
import { LoginPageComponent } from "../module/login/login-page/login-page.component";
import { TeamSettingsScaffoldComponent } from "../module/team/settings/team-settings-scaffold.component";
import { UserSettingsPageComponent } from "../module/user/page/user-settings-page.component";
import { SpaceDetailsPageComponent } from "../module/space/details/space-details-page.component";
import { SpacesPageComponent } from "../module/space/page/spaces-page.component";
import { MemberDetailsPageComponent } from "../module/member/details/member-details-page.component";
import { MembersPageComponent } from "../module/member/page/members-page.component";
import { SpinnerComponent } from "typedb-cloud-framework";
import { GcpMarketplaceIntegrationComponent } from "../module/team/gcp-marketplace-integration/gcp-marketplace-integration.component";
import {
    AzureMarketplaceIntegrationComponent
} from "../module/team/azure-marketplace-integration/azure-marketplace-integration.component";
import {
    AwsMarketplaceIntegrationComponent
} from "../module/team/aws-marketplace-integration/aws-marketplace-integration.component";
import { GetStartedPageComponent } from "../module/team/get-started/get-started-page.component";
import { OverviewPageComponent } from "../module/team/overview/overview-page.component";
import { NgModule } from "@angular/core";
import { ClusterLogsPageComponent } from "../module/cluster/log/cluster-logs-page.component";
import { TeamSettingsGeneralComponent } from "../module/team/settings/general/team-settings-general.component";
import { TeamSettingsBillingComponent } from "../module/team/settings/billing/team-settings-billing.component";
import { SecurityCenterScaffoldComponent } from "../module/team/security/security-center-scaffold.component";
import { SecurityCenterOverviewComponent } from "../module/team/security/overview/security-center-overview.component";
import { SecurityCenterConfigComponent } from "../module/team/security/./security-config/security-center-config.component";

const domains = {
    clusters: "clusters",
    spaces: "spaces",
    squads: "squads",
    members: "members",
    settings: "settings",
    security: "security",
    getStarted: "get-started",
    overview: "overview",
} as const;

export type RouteDomain = typeof domains[keyof typeof domains];

export const routeDomains = domains;

export const routes: Routes = [
    { path: "", component: SpinnerComponent, pathMatch: "full", canActivate: [genericGuard] },
    { path: "sign-in", component: LoginPageComponent, canActivate: [loggedOutGuard], title: "Sign In" },
    { path: "sign-up", component: LoginPageComponent, canActivate: [loggedOutGuard], title: "Sign Up" },
    { path: "forgot-password", component: LoginPageComponent, title: "Forgot Password" },
    { path: "verify-email", component: LoginPageComponent, title: "Verify Email" },
    { path: "mfa", component: LoginPageComponent, canActivate: [mfaGuard], title: "MFA" },
    {
        path: "setup",
        children: [
            { path: "", component: SetupComponent, pathMatch: "full", canActivate: [setupGuard], title: "Setup" },
            { path: "project", component: SetupProjectComponent, canActivate: [teamJoinedNotSurveyedGuard], data: { variant: "standalone" }, title: "Project Info" },
        ]
    },
    { path: "reset-password", component: PasswordResetPageComponent, canActivate: [loggedOutGuard], title: "Reset Password" },
    { path: "org", redirectTo: "team" },
    {
        path: "team/:team-id",
        canActivate: [setupCompletedGuard],
        component: TeamSubsiteScaffoldComponent,
        children: [
            { path: "", canActivate: [getStartedOverviewGuard], component: SpinnerComponent, pathMatch: "full" },
            {
                path: "settings",
                component: TeamSettingsScaffoldComponent,
                data: { domain: domains.settings, },
                title: "Team Settings",
                children: [
                    { path: "", redirectTo: "general", pathMatch: "full" },
                    { path: "general", component: TeamSettingsGeneralComponent, data: { settingsSection: "general" } },
                    { path: "billing", component: TeamSettingsBillingComponent, data: { settingsSection: "billing" } },
                ]
            },
            {
                path: "security",
                component: SecurityCenterScaffoldComponent,
                data: { domain: domains.security },
                title: "Security Center",
                children: [
                    { path: "", redirectTo: "overview", pathMatch: "full" },
                    { path: "overview", component: SecurityCenterOverviewComponent, data: { securitySection: "overview" } },
                    { path: "config", component: SecurityCenterConfigComponent, data: { securitySection: "config" } },
                ]
            },
            { path: "get-started", component: GetStartedPageComponent, data: { domain: domains.getStarted, }, title: "Get Started" },
            { path: "overview", component: OverviewPageComponent, data: { domain: domains.overview, }, title: "Overview" },
            { path: "clusters", component: ClustersPageComponent, data: { domain: domains.clusters, }, title: "Clusters" },
            { path: "clusters/deploy", component: ClusterDeploymentPageComponent, data: { domain: domains.clusters, }, title: "Deploy Cluster" },
            { path: "spaces/:space-id/clusters/:cluster-id", component: ClusterDetailsPageComponent, data: { domain: domains.clusters, }, title: "Clusters" },
            { path: "spaces/:space-id/clusters/:cluster-id/logs", component: ClusterLogsPageComponent, data: { domain: domains.clusters, }, title: "Clusters" },
            { path: "spaces/:space-id/clusters/:cluster-id/servers/:server-id", component: ServerDetailsPageComponent, data: { domain: domains.clusters, }, title: "Clusters" },
            { path: "spaces", component: SpacesPageComponent, data: { domain: domains.spaces, }, title: "Spaces" },
            { path: "spaces/:space-id", component: SpaceDetailsPageComponent, data: { domain: domains.spaces, }, title: "Spaces" },
            { path: "members", component: MembersPageComponent, data: { domain: domains.members, }, title: "Members" },
            { path: "members/:user-id", component: MemberDetailsPageComponent, data: { domain: domains.members }, title: "Members" },
            { path: "projects", redirectTo: "spaces" },

            { path: "**", component: Internal404PageComponent, title: "404" },
        ],
    },
    {
        path: "marketplace",
        children: [
            {
                path: "gcp",
                canActivate: [setupCompletedGuard],
                component: GcpMarketplaceIntegrationComponent,
            },
            {
                path: "azure",
                canActivate: [setupCompletedGuard],
                component: AzureMarketplaceIntegrationComponent,
            },
            {
                path: "aws",
                canActivate: [setupCompletedGuard],
                component: AwsMarketplaceIntegrationComponent,
            }
        ]
    },
    {
        path: "settings",
        canActivate: [setupCompletedGuard],
        component: TeamSubsiteScaffoldComponent,
        children: [{ path: "", component: UserSettingsPageComponent, title: "User Settings" }],
    },
    { path: "**", component: _404PageComponent, title: "404" },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { anchorScrolling: "enabled" })],
    exports: [RouterModule],
})
export class CloudRoutingModule {}
